"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebRtcDriver = void 0;
const tslib_1 = require("tslib");
const state_flow_1 = require("@video/state-flow");
const zod_1 = require("zod");
const bind_1 = require("../../../../internal/utils/bind");
const browser_support_1 = require("../../../../internal/utils/browser-support");
const logger_1 = require("../../../../internal/utils/logger");
const video_client_1 = require("../../../../internal/video-client");
const Audio = tslib_1.__importStar(require("../../state-flow/controls/audio"));
const Playback = tslib_1.__importStar(require("../../state-flow/controls/playback"));
const WebRtc = tslib_1.__importStar(require("../../state-flow/drivers/webrtc"));
const encoding_selectors_1 = require("../encoding-selectors");
const register_1 = require("../register");
const common_1 = require("./common");
//#region Player Options
const OptionsWebrtcSchema = common_1.optionsCommonSchema.extend({
    id: zod_1.z.literal("webrtc"),
}).readonly();
//#endregion
let webrtcIsSupported = false;
(0, browser_support_1.supportsMediasoupWebrtc)("WebRTC").then((supported) => {
    webrtcIsSupported = supported;
});
// returns wrapper to make sure that we use native hls
function wrap(fn) {
    const wrapper = (ss, sf) => {
        // check if webRtc driver is in use
        if (!WebRtc.InUse.is(sf.webRtc)) {
            return state_flow_1.Result.ignore();
        }
        return fn(ss, sf);
    };
    Reflect.defineProperty(wrapper, "name", { configurable: true, value: fn.name });
    return wrapper;
}
async function waitForSource(provider) {
    if (provider.source != null) {
        return provider.source;
    }
    return new Promise((resolve, reject) => {
        provider.once("source", (source) => {
            resolve(source);
        });
    });
}
class WebRtcDriver {
    constructor(ctx) {
        this.ctx = ctx;
        // readonly debugInfo: unknown;
        this.encodingSelector = new encoding_selectors_1.HighestBitrateSelector();
        // metadata: unknown;
        this.availableEncodings = [];
        (0, bind_1.makeBounded)(this);
        this.vc = ctx.videoClient;
    }
    //
    // get host(): VideoElement {
    //   if (this.sf.host.element == null) {
    //     throw new Error("host is null");
    //   }
    //   return this.sf.host.element as VideoElement;
    // }
    //
    // get active(): boolean {
    //   return WebRtc.InUse.is(this.sf.webRtc);
    // }
    static isManifestSupported(manifest) {
        return (manifest === null || manifest === void 0 ? void 0 : manifest.formats["webrtc"]) != null;
    }
    static isHostSupported(host) {
        return (0, common_1.isVideoElement)(host);
    }
    static isPlatformSupported() {
        return webrtcIsSupported;
    }
    initStateFlow(sf) {
        sf.handleEnter(WebRtc.Prepared, this.prepare);
        sf.handleEnter(WebRtc.InUse, this.activate);
        sf.handleExit(WebRtc.InUse, this.deactivate);
        // sf.onCommit(Host.Attached, this.onHostAttached);
        // sf.handleExit(Host.Attached, wrap(this.onHostDetaching));
        sf.handleEnter(Playback.Playing, wrap(common_1.startPlayingVideoElement));
        sf.handleExit(Playback.Playing, wrap(common_1.stopPlayingVideoElement));
        sf.handleEnter(Audio.UnMuted, wrap(common_1.unmuteVideoElement));
        sf.handleChanges(Audio.UnMuted, wrap(common_1.changeVolumeVideoElement));
        sf.handleExit(Audio.UnMuted, wrap(common_1.muteVideoElement));
    }
    prepare(states) {
        return state_flow_1.Result.ok();
    }
    activate({ host, manifest, webRtc }) {
        const el = (0, common_1.getVideoElement)(host.element);
        if (manifest.json == null) {
            throw Error("manifest is null");
        }
        const format = manifest.json.formats["webrtc"];
        return state_flow_1.Result.transition(this.joinCall(format, el, webRtc.options.timeout));
    }
    deactivate({ host }) {
        var _a;
        const el = (0, common_1.getVideoElement)(host.element);
        // some cleanup
        (_a = this.call) === null || _a === void 0 ? void 0 : _a.dispose("driver is deactivated");
        el.srcObject = null;
        return state_flow_1.Result.ok();
    }
    async [Symbol.dispose]() {
        var _a;
        (_a = this.call) === null || _a === void 0 ? void 0 : _a.dispose("driver is disposed");
    }
    async joinCall(format, el, timeout) {
        if (format.origin.uri == null) {
            return state_flow_1.Result.reject("invalid sfu uri");
        }
        const joinSfuParams = {
            call: {
                id: format.origin.callId,
                sfu: {
                    uri: format.origin.uri,
                    httpUri: format.origin.httpUri,
                    region: format.origin.region,
                    version: format.origin.version,
                },
                turn: format.origin.turn,
                support: "ovh",
            },
            user: {
                userId: format.origin.token,
                scope: "viewer",
                displayName: "",
                authorizeToken: format.origin.token,
            },
        };
        const sfuOptions = {
            rsrc: format.origin.rsrc,
            xkey: format.origin.publicKey,
            bpeerId: format.origin.peerId,
            joinUrl: format.origin.location,
        };
        const joinCallOptions = {
            token: format.origin.token,
            playerOptions: { dontEmitPlayer: true },
        };
        if (format.origin.streamNames.length === 0) {
            return state_flow_1.Result.reject("no stream names in  manifest");
        }
        const streamName = format.origin.streamNames[0];
        if (format.origin.streamNames.length > 1) {
            this.ctx.logger.warn("multiple stream names in manifest. use first one");
        }
        const call = this.call = await this.vc.internalJoinCall(format.origin.callId, joinSfuParams, { ...sfuOptions, ...joinCallOptions }, (0, logger_1.extendContext)(this.ctx, video_client_1.VideoClient));
        return new Promise((resolve, reject) => {
            call.once("error", (err) => {
                reject(err);
            });
            call.on("streamAdded", async (provider) => {
                if (provider.streamName !== streamName) {
                    return;
                }
                (0, common_1.updateSource)(el, await waitForSource(provider.stream));
                el.srcObject = await waitForSource(provider.stream);
                provider.stream.on("source", common_1.updateSource.bind(null, el));
                resolve(state_flow_1.Result.ok());
            });
            setTimeout(() => resolve(state_flow_1.Result.reject("timeout")), timeout * 5);
        });
    }
}
exports.WebRtcDriver = WebRtcDriver;
WebRtcDriver.displayName = "WebRtcDriver";
WebRtcDriver.driverName = "webrtc";
(0, register_1.registerVideoDrivers)(WebRtcDriver);
//# sourceMappingURL=webrtc.js.map